import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, PaymentButton } from '@k3imagine/self-serve-components';

import GlobalContext from '../../../state/GlobalContext';
import { useLocalPrinter } from '../../../state/useLocalPrinter';
import { webSerial } from '../../../services/printer/local/web-serial.service';
import {
  BAUD_RATES,
  localPrinter
} from '../../../services/printer/local/local-printer.service';
import {
  getHeaderSettings,
  HeaderSettings,
  saveHeaderSettings
} from '../../../services/printer/local/local-printer.settings';
import { resolveHeader } from '../../../services/printer/local/receipt-layout';
import * as S from '../SupervisorModal.styles';

const ADDRESS_LINES = 3;

/**
 * Connect, test and configure the printer attached to this till.
 * Shown when "Local device" is chosen in place of a connector.
 */
const LocalPrinterSettings = () => {
  const { t } = useTranslation();
  const { shop } = useContext(GlobalContext);
  const printer = useLocalPrinter();
  const [busy, setBusy] = useState<boolean>(false);
  const [baudRate, setBaudRate] = useState<number>(webSerial.getBaudRate());
  const [header, setHeader] = useState<HeaderSettings>(getHeaderSettings());

  // What prints when a field is left blank: the shop record.
  const fromShop = resolveHeader(shop, {
    shopName: '',
    addressLines: [],
    vatNumber: '',
    footer: ''
  });

  /**
   * Every action reports its failure into the panel. A button that silently
   * does nothing is indistinguishable from a broken one.
   */
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    webSerial.setError(null);
    try {
      await action();
    } catch (err) {
      webSerial.setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const updateHeader = (patch: Partial<HeaderSettings>) => {
    const next = { ...header, ...patch };
    setHeader(next);
    saveHeaderSettings(next);
  };

  const updateAddressLine = (index: number, value: string) => {
    const lines = Array.from(
      { length: ADDRESS_LINES },
      (_, i) => header.addressLines[i] || ''
    );
    lines[index] = value;
    updateHeader({ addressLines: lines });
  };

  if (!printer.supported) {
    return (
      <S.LocalPrinterPanel>
        <S.PanelText>{t('LocalPrinter.Unsupported')}</S.PanelText>
      </S.LocalPrinterPanel>
    );
  }

  return (
    <S.LocalPrinterPanel>
      <S.PrinterStatus connected={printer.connected}>
        {printer.connected
          ? t('LocalPrinter.Connected', { port: printer.portLabel })
          : t('LocalPrinter.NotConnected')}
      </S.PrinterStatus>
      {printer.lastError && (
        <S.PrinterError>{printer.lastError}</S.PrinterError>
      )}

      <S.PrinterButtons>
        {!printer.connected && (
          <PaymentButton
            label={t('LocalPrinter.Connect')}
            disabled={busy}
            // Must stay a direct click handler: the port picker needs a gesture.
            onClicked={() => run(() => webSerial.connect())}
          />
        )}
        {printer.connected && (
          <>
            <PaymentButton
              label={t('LocalPrinter.TestPrint')}
              disabled={busy}
              onClicked={() => run(() => localPrinter.printTestPage(shop))}
            />
            <PaymentButton
              label={t('LocalPrinter.OpenDrawer')}
              disabled={busy}
              onClicked={() => run(() => localPrinter.openDrawer())}
            />
            <S.FullWidth>
              <PaymentButton
                label={t('LocalPrinter.Disconnect')}
                theme="Dark"
                disabled={busy}
                onClicked={() => run(() => webSerial.disconnect())}
              />
            </S.FullWidth>
          </>
        )}
      </S.PrinterButtons>

      {printer.connected && (
        <>
          <S.PanelLabel>{t('LocalPrinter.LineSpeed')}</S.PanelLabel>
          <S.BaudRow>
            {BAUD_RATES.map(rate => (
              <S.BaudButton
                key={rate}
                type="button"
                selected={rate === baudRate}
                disabled={busy}
                onClick={() =>
                  run(async () => {
                    await localPrinter.useBaudRate(rate);
                    setBaudRate(webSerial.getBaudRate());
                  })
                }
              >
                {rate}
              </S.BaudButton>
            ))}
          </S.BaudRow>
          <PaymentButton
            label={t('LocalPrinter.FindLineSpeed')}
            disabled={busy}
            onClicked={() => run(() => localPrinter.findBaudRate())}
          />
          <S.HintText>{t('LocalPrinter.FindLineSpeedHint')}</S.HintText>
        </>
      )}

      <S.PanelLabel>{t('LocalPrinter.ReceiptHeader')}</S.PanelLabel>
      <S.HintText>{t('LocalPrinter.ReceiptHeaderHint')}</S.HintText>
      <S.FieldRow>
        <Input
          value={header.shopName}
          placeholder={t('LocalPrinter.ShopName')}
          onChange={({ value }: { value: string }) =>
            updateHeader({ shopName: value })
          }
        />
      </S.FieldRow>
      {Array.from({ length: ADDRESS_LINES }, (_, i) => (
        <S.FieldRow key={`address-${i}`}>
          <Input
            value={header.addressLines[i] || ''}
            placeholder={
              fromShop.addressLines[i] ||
              t('LocalPrinter.AddressLine', { number: i + 1 })
            }
            onChange={({ value }: { value: string }) =>
              updateAddressLine(i, value)
            }
          />
        </S.FieldRow>
      ))}
      <S.FieldRow>
        <Input
          value={header.vatNumber}
          placeholder={fromShop.vatNumber || t('LocalPrinter.VatNumber')}
          onChange={({ value }: { value: string }) =>
            updateHeader({ vatNumber: value })
          }
        />
      </S.FieldRow>
      <S.FieldRow>
        <Input
          value={header.footer}
          placeholder={t('LocalPrinter.Footer')}
          onChange={({ value }: { value: string }) =>
            updateHeader({ footer: value })
          }
        />
      </S.FieldRow>
    </S.LocalPrinterPanel>
  );
};

export default LocalPrinterSettings;
